/**
 * matchRanking — the one ordering the whole match-trail experience reads.
 *
 * The callout, the traced trails, the selected beacon, the deck's `MATCH 2 OF 8`
 * and the analytics `rank` all used to derive their own "which match is the
 * important one" answer. `pickPrimaryOpportunity` sorted by raw distance, the
 * trail overlay took the first N in array order, and the reveal opened whatever
 * the caller happened to pass. Three orderings meant the loud callout could
 * describe one trader while the brightest trail pointed at another.
 *
 * Pure on purpose: no Mapbox, no React, no clock. Everything here is a function
 * of the match payload, so the ordering can be asserted directly.
 */

import {
  getUserLngLat,
  idStr,
  matchCardCount,
  matchDirection,
} from "./tradeOpportunity";

/** Never animate more trails than this at once, however many matches exist. */
export const MAX_ANIMATED_TRAILS = 3;

/**
 * Rounded approximate distance in metres, or Infinity when we have none.
 *
 * The server already rounds `approxDistanceMeters` to the nearest 100 m and
 * derives it from a coordinate the client also holds, so using it here reveals
 * nothing further. We deliberately do NOT recompute a sharper distance from the
 * raw coordinates — a tie-break is not worth un-rounding a privacy decision.
 * Missing distance sorts last rather than first, so an unknown never outranks a
 * known-nearby collector.
 */
export const approxMetres = (trader) => {
  const m = trader?.approxDistanceMeters;
  return typeof m === "number" && Number.isFinite(m) ? m : Infinity;
};

/** Does this match have a coordinate we can actually put on the map? */
export const hasUsablePoint = (trader) => getUserLngLat(trader) !== null;

/**
 * Deterministic ranking: mutual → actionable cards → rounded distance → id.
 *
 * The id tie-break is what makes this stable: without it two matches with the
 * same direction, card count and rounded distance could swap places between
 * discovery polls, and the deck would appear to shuffle itself while open.
 */
export const rankMatches = (matches = []) =>
  [...(matches || [])].filter(Boolean).sort((a, b) => {
    const am = matchDirection(a) === "mutual" ? 0 : 1;
    const bm = matchDirection(b) === "mutual" ? 0 : 1;
    if (am !== bm) return am - bm;

    const ac = matchCardCount(a);
    const bc = matchCardCount(b);
    if (ac !== bc) return bc - ac;

    const ad = approxMetres(a);
    const bd = approxMetres(b);
    if (ad !== bd) return ad - bd;

    return idStr(a?.id).localeCompare(idStr(b?.id));
  });

/**
 * Count mode drives CTA copy, entrance shape and where the flow lands.
 * 0 → honest empty state · 1 → straight to the reveal · 2–4 → deck ·
 * 5+ → deck over the full queue with only the top 3 traced.
 */
export const countMode = (n) =>
  n === 0 ? "none" : n === 1 ? "one" : n <= 4 ? "few" : "many";

/**
 * The slice that gets an animated trail. Matches with no usable coordinate are
 * excluded from tracing but stay in the queue — they are still real matches,
 * they just have no map point to draw to.
 */
export const animatedTargets = (queue = [], max = MAX_ANIMATED_TRAILS) =>
  (queue || []).filter(hasUsablePoint).slice(0, Math.max(0, max));

/** Just the ids, which is what the map layers actually need. */
export const animatedTargetIds = (queue, max) =>
  animatedTargets(queue, max).map((t) => idStr(t.id));

/** The matched subset of the loaded users, ranked. This is the frozen queue. */
export const buildMatchQueue = (users = [], matchedUserIds = []) => {
  const matched = new Set((matchedUserIds || []).map(idStr));
  if (matched.size === 0) return [];
  return rankMatches(
    (users || []).filter((u) => u && matched.has(idStr(u.id)))
  );
};

/** CTA copy per count mode — the label is the promise the entrance keeps. */
export const ctaLabelFor = (count) => {
  const mode = countMode(count);
  if (mode === "one") return "Reveal match";
  if (mode === "few") return `Show ${count} matches`;
  if (mode === "many") return `Explore ${count} matches`;
  return "";
};

/**
 * Distance a person can act on. Under 1 km we round to the nearest 50 m so the
 * number never implies a doorstep; above it we use one decimal of a kilometre.
 *
 * The rounding alone did not keep that promise. The server rounds to 100 m, so a
 * collector in the first bucket arrives here as `0`, and `Math.round(0 / 50) * 50`
 * printed "~0 m away" — a doorstep stated outright. Everything below the first
 * step is therefore clamped to it: "~50 m away" is the closest this will ever read.
 */
const MIN_APPROX_METRES = 50;

export const formatApproxDistance = (metres) => {
  if (typeof metres !== "number" || !Number.isFinite(metres)) return null;
  if (metres >= 1000) return `~${(metres / 1000).toFixed(1)} km away`;
  const rounded = Math.round(metres / 50) * 50;
  return `~${Math.max(rounded, MIN_APPROX_METRES)} m away`;
};

export const DIRECTION_LABEL = {
  mutual: "Two-way match",
  they_have: "They have something you want",
  they_want: "They want from your Trade Binder",
};

/** Narrow phones get the shorter half of each label, never a truncated one. */
export const DIRECTION_LABEL_SHORT = {
  mutual: "Two-way match",
  they_have: "They have one you want",
  they_want: "They want one of yours",
};

export const directionLabel = (trader, { narrow = false } = {}) => {
  const table = narrow ? DIRECTION_LABEL_SHORT : DIRECTION_LABEL;
  return table[matchDirection(trader)] || table.they_have;
};

/** "1 card connects your collections" / "3 cards connect your collections". */
export const cardsConnectCopy = (trader) => {
  const n = matchCardCount(trader);
  return `${n} card${n === 1 ? "" : "s"} connect${n === 1 ? "s" : ""} your collections`;
};

/** Display name for a collector, or the anonymised stand-in. */
export const collectorName = (trader, { anonymize = false } = {}) => {
  if (anonymize) return "Nearby trusted collector";
  return (
    trader?.username ||
    `${trader?.name?.first || ""} ${trader?.name?.last || ""}`.trim() ||
    "This collector"
  );
};

/** Up to two initials for the avatar coin. */
export const collectorInitials = (trader, { anonymize = false } = {}) => {
  if (anonymize) return "?";
  const name = collectorName(trader);
  const parts = name.split(/[\s-]+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => p[0]).join("");
  return letters ? letters.toUpperCase() : "?";
};

/**
 * The trust line under the collector's name.
 *
 * Every part is optional and the join drops the blanks, so a brand-new
 * unverified collector with a hidden location reads "New to trading" rather
 * than "· · ". Nothing here is invented: an absent field produces no text.
 */
export const collectorMetaLine = (trader, { anonymize = false } = {}) => {
  const verified = !!trader?.verified && !anonymize;
  const trades = trader?.successfulTrades;
  const tradesLabel =
    typeof trades === "number" && trades > 0
      ? `${trades} completed trade${trades === 1 ? "" : "s"}`
      : "New to trading";
  const proximity = hasUsablePoint(trader)
    ? formatApproxDistance(approxMetres(trader))
    : "Location approximate";

  return [verified ? "Verified" : null, tradesLabel, proximity]
    .filter(Boolean)
    .join(" · ");
};

/** Live-region string for a session opening. */
export const matchesFoundAnnouncement = (count) =>
  count === 1 ? "1 trade match found" : `${count} trade matches found`;

/** Live-region string for a selection change. */
export const matchSelectedAnnouncement = (index, total) =>
  `Match ${index + 1} of ${total} selected`;

/**
 * The one status message a live session shows. In many-mode it has to explain
 * why only three trails were drawn, or the untraced matches read as missing.
 */
export const sessionStatusCopy = (count, animatedCount) => {
  const mode = countMode(count);
  if (count === 1) return "1 trade match found";
  if (mode === "many") {
    return `${count} trade matches · ${animatedCount} best fits shown`;
  }
  return `${count} trade matches found`;
};
